import React, { useState, useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import right1 from "../../assets/REMA/whatToExpect/right-1.png";
import { Plus } from "phosphor-react";
import { ATTENDEE_CATEGORIES } from "./constants";
import { ScrollTrigger } from "gsap/all";

const WhoShouldAttend = () => {
  const [openAccordion, setOpenAccordion] = useState(0); // First item open by default
  const accordionRef = useRef(null);
  const lineRefs = useRef([]);
  const stickyImageRef = useRef(null);
  const headerRef = useRef(null);

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? -1 : index);
  };

  // GSAP animations
  useGSAP(() => {
    const accordionItems =
      accordionRef.current?.querySelectorAll(".accordion-item");
    if (accordionItems) {
      gsap.set(accordionItems, { opacity: 0, y: 30 });

      gsap.to(accordionItems, {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "power2.out",
        delay: 0.2,
        scrollTrigger: {
          trigger: accordionRef.current,
          start: "top 80%",
          once: true,
        },
      });
    }
    gsap.matchMedia().add("(min-width: 1024px)", () => {
      // // Pinning the header
      ScrollTrigger.create({
        trigger: accordionRef.current,
        start: "top 80",
        end: "bottom 50%",
        pin: stickyImageRef.current,
        pinType: "fixed",
        // pinSpacing: true, // Adds a spacer element to prevent content from jumping
        // markers: true, // Uncomment for visual debugging
        // scrub: true,
        // invalidateOnRefresh: true, // Recalculate on resize/refresh
        fastScrollEnd: true,
        anticipatePin: 1,
      });
    });
  }, []);

  // useGSAP(() => {
  //   let pinScrollTrigger = null;

  //   const createPinScrollTrigger = () => {
  //     if (
  //       window.matchMedia("(min-width: 1024px)").matches &&
  //       stickyImageRef.current
  //     ) {
  //       pinScrollTrigger = ScrollTrigger.create({
  //         trigger: accordionRef.current,
  //         start: "top 80%",
  //         end: "bottom 50%",
  //         pin: stickyImageRef.current,
  //         pinSpacing: false,
  //       });
  //     }
  //   };

  //   const destroyPinScrollTrigger = () => {
  //     if (pinScrollTrigger) {
  //       pinScrollTrigger.kill();
  //       pinScrollTrigger = null;
  //     }
  //   };

  //   // Create on mount
  //   createPinScrollTrigger();

  //   // Listen for resize
  //   const mediaQuery = window.matchMedia("(min-width: 1024px)");
  //   const handleResize = () => {
  //     if (mediaQuery.matches) {
  //       if (!pinScrollTrigger) createPinScrollTrigger();
  //     } else {
  //       destroyPinScrollTrigger();
  //     }
  //   };

  //   mediaQuery.addEventListener("change", handleResize);

  //   // Cleanup on unmount
  //   return () => {
  //     mediaQuery.removeEventListener("change", handleResize);
  //     destroyPinScrollTrigger();
  //   };
  // }, []);

  const handleMouseEnter = (index) => {
    const line = lineRefs.current[index];
    if (line) {
      gsap.fromTo(
        line,
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 0.4,
          ease: "power2.out",
          transformOrigin: "left center",
        }
      );
    }
  };

  const handleMouseLeave = (index) => {
    const line = lineRefs.current[index];
    if (line) {
      gsap.to(line, {
        scaleX: 0,
        duration: 0.3,
        ease: "power2.out",
        transformOrigin: "left center",
      });
    }
  };

  return (
    <section className="min-h-screen flex items-center justify-center">
      <div className="container mx-auto px-4 md:px-10 py-16 md:py-[100px] font-sora">
        <div className="flex flex-col lg:flex-row gap-16 md:gap-[150px]">
          {/* Left side - Image */}
          <div className="lg:w-[50%]">
            <div
              ref={stickyImageRef}
              className="will-change-transform"
              style={{ transition: "0s" }}
            >
              <h2
                className="text-3xl lg:text-[32px] font-bold text-gray-900 mb-8"
                ref={headerRef}
              >
                WHO SHOULD ATTEND
              </h2>
              <p className="text-sm mb-8 text-[#1A1A1A] leading-7">
                The REMA Conference 2025 brings together the full spectrum of
                stakeholders driving Africa’s urban future. Whether you build,
                manage, regulate, innovate, or invest in real estate and
                PropTech, this is your platform to connect, learn, and lead.
              </p>
              <div className="  bg-gray-200 flex items-center justify-center aspect-[530/354] border w-full">
                <img
                  src={right1}
                  alt="Who Should Attend"
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
              {/* Green accent bar */}
              <div className="  w-full h-4 bg-[#A5D246] mt-10 "></div>
            </div>
          </div>

          {/* Right side - Accordion */}
          <div
            className={`lg:pt-[${headerRef.current?.offsetHeight}px]`}
            ref={accordionRef}
          >
            <div className="flex flex-col gap-8">
              <div className="w-full h-[1px] bg-[#11302D] opacity-20" />
              {ATTENDEE_CATEGORIES.map((category, index) => (
                <React.Fragment key={index}>
                  <div
                    className="group space-y-8 accordion-item"
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={() => handleMouseLeave(index)}
                  >
                    <div className=" overflow-hidden transition-all duration-200">
                      <button
                        className="w-full text-left flex justify-between items-center gap-4"
                        onClick={() => toggleAccordion(index)}
                      >
                        <p className="font-semibold text-gray-900 text-lg">
                          {category.title}
                        </p>
                        <div>
                          {
                            <Plus
                              color="#1A1A1A"
                              size={20}
                              weight="bold"
                              className={`transform transition-transform duration-300 ${
                                openAccordion === index ? "rotate-45" : ""
                              }`}
                            />
                          }
                        </div>
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          openAccordion === index
                            ? "max-h-96 opacity-100 mt-6"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="pb-4 text-gray-600 font-inter">
                          {category.content}
                        </div>
                      </div>
                    </div>
                    {/* Animated dividing line */}
                    <div className="relative w-full h-[1px] bg-[#11302d4b]">
                      <div className="absolute inset-y-0 left-0 h-[2px] bg-[#A5D246] origin-left transition-all duration-300 ease-out w-0 group-hover:w-full" />
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoShouldAttend;
